import React, {memo, useEffect, useState} from "react";
import {isEmpty} from "lodash";
import PropTypes from "prop-types";
import {
  Col,
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import {useSelector, useDispatch} from "react-redux";
import {toast} from "react-toastify";
import {
  requestCategories,
  requestSensorGroupsByCategory,
  requestControllerSensorData,
  resetControllerSensorData,
} from "../../redux/Actions/controllers.actions";
import {requestClaimController} from "../../redux/Actions/sites.actions";
import {
  getCategoriesList,
  getControllerSensorData,
} from "../../redux/Selectors/controllers.selectors";
import {getLocationsList} from "../../redux/Selectors/sites.selectors";
import ZeusButton from "../Button";
import InputCreator from "../Forms/components/InputCreator";
import SelectCreator from "../Forms/components/SelectCreator";
import Loader from "../Loader";
import ProtectedContent from "../protectedContent";

const ClaimController = (props) => {
  const {onCancel, open} = props;
  const dispatch = useDispatch();

  const [serial, setSerialNumber] = useState(undefined);
  const [check_code, setCheckCode] = useState(undefined);
  const [name, setName] = useState(undefined);
  const [location, setLocation] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [eventRule, setEventRule] = useState(undefined);
  const [groupList, setGroupList] = useState([]);
  const [groups, setGroups] = useState([]);

  const locations = useSelector(getLocationsList);
  const categories = useSelector(getCategoriesList);
  const eventRuleOptions = useSelector(getControllerSensorData);

  useEffect(() => {
    dispatch(requestCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    dispatch(requestControllerSensorData());
    // Runs when the modal closes (or unmounts): cancels an in-flight request
    // in the saga and clears the stored options so the next open starts clean.
    return () => {
      dispatch(resetControllerSensorData());
    };
  }, [open, dispatch]);

  const handleCategoryChange = (e) => {
    const newValue = e.target.value;

    setCategory(newValue);
    setGroups([]);

    if (newValue) {
      dispatch(requestSensorGroupsByCategory(newValue, setGroupList));
    }
  };

  const submitHandler = () => {
    if (!name || !serial || !check_code || !location || !category || isEmpty(groups)) {
      toast.error("Please fill all the required fields");
      return;
    }

    const payload = {
      name,
      serial,
      check_code,
      location,
      category_id: category,
      groups_id: groups,
      event_rule: eventRule || null,
    };

    dispatch(requestClaimController(payload, onCancel));
  };

  return (
    <Modal unmountOnClose={true} isOpen={open} backdrop="static">
      <Loader />

      <ModalHeader>Claim Appliance</ModalHeader>

      <ModalBody>
        <div className="pl-lg-4">
          <Row>
            <ProtectedContent perms={["view_location"]}>
              <Col md="12">
                <FormGroup>
                  <SelectCreator
                    label="Location"
                    placeholder="Choose a location"
                    name="locations"
                    options={locations}
                    onChange={(e) => setLocation(e.target.value)}
                    value={location}
                    large
                    isRequired
                    isSearchable
                  />
                </FormGroup>
              </Col>
            </ProtectedContent>

            <ProtectedContent perms={["view_location"]}>
              <Col md="12">
                <FormGroup>
                  <SelectCreator
                    label="Category"
                    placeholder="Choose a category"
                    name="category"
                    options={categories}
                    onChange={handleCategoryChange}
                    value={category}
                    large
                    isRequired
                  />
                </FormGroup>
              </Col>
            </ProtectedContent>

            <Col md="12">
              <FormGroup>
                <SelectCreator
                  label="Groups"
                  placeholder="Choose Group(s)"
                  name="group"
                  options={groupList}
                  onChange={(e) => setGroups(e.target.value)}
                  value={groups}
                  isMulti={true}
                  large
                  isRequired
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <SelectCreator
                  label="Event Rule"
                  placeholder="Choose an Event Rule"
                  name="eventRule"
                  options={eventRuleOptions}
                  onChange={(e) => setEventRule(e.target.value)}
                  value={eventRule}
                  large
                  isSearchable
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <InputCreator
                  label="Name"
                  placeholder="Device Name"
                  name="name"
                  onBlur={(e) => setName(e.target.value)}
                  large
                  isRequired
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <InputCreator
                  label="Serial Number"
                  placeholder="Enter serial number"
                  name="serialNumber"
                  onBlur={(e) => setSerialNumber(e.target.value)}
                  large
                  isRequired
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <InputCreator
                  label="Check Code"
                  placeholder="Enter Check Code"
                  name="checkCode"
                  onBlur={(e) => setCheckCode(e.target.value)}
                  large
                  isRequired
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      </ModalBody>

      <ModalFooter>
        <ZeusButton onClick={submitHandler} data-cy="submit-btn">
          CLAIM
        </ZeusButton>

        <ZeusButton onClick={onCancel} data-cy="cancel-btn" className="info-small">
          CANCEL
        </ZeusButton>
      </ModalFooter>
    </Modal>
  );
};

ClaimController.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default memo(ClaimController);
